import { createSelector } from 'reselect';
import { ExternalPricelistDto, ResourceType, UserGroupPermissionDto } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { getPermission, notNull } from '../../../utils';
import { getAvailableResources, getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { getSelectedUserGroup, getUserGroupPermissions } from '../../usergroup/redux/userGroupSelectors';
import { ExternalPricelistTreeItem } from '../externalPriceTypes';

export const getUnfilteredExternalPricelists = (state: RootState) =>
    state.externalPricelist.externalPricelists;

export const getExternalPricelists = createSelector(
    [getUnfilteredExternalPricelists, getCurrentUserPermissions],
    (unfilteredExternalPricelists, userPermissions) => {
        if (userPermissions.isAdmin) {
            return unfilteredExternalPricelists;
        }

        const externalPricelists: { [pricelistId: number]: ExternalPricelistDto } = {};

        const externalPricelistPermissions = userPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.ExternalPricelist
        );

        // Zero is a special record id which applies to all records
        if (externalPricelistPermissions?.recordPermissions.find(x => x.recordId === 0)?.canCreate) {
            return unfilteredExternalPricelists;
        }

        if (externalPricelistPermissions != null) {
            for (const plp of externalPricelistPermissions.recordPermissions) {
                if (plp.recordId != null && plp.canRead) {
                    const externalPricelist = unfilteredExternalPricelists[plp.recordId];
                    if (externalPricelist != null) {
                        externalPricelists[plp.recordId] = externalPricelist;
                    }
                }
            }
        }

        return externalPricelists;
    }
);

export const getSelectedExternalPricelistId = (state: RootState) =>
    state.externalPricelist.ui.selectedExternalPricelistId;
export const getExternalPricelistTree = (state: RootState) => {
    const externalPricelists = Object.values(getExternalPricelists(state));

    const externalPricelistTreeItems: ExternalPricelistTreeItem[] = externalPricelists
        .map(pl => ({
            id: pl.externalPricelistId,
            name: pl.name ?? '??',
            ref: pl.externalPricelistRef ?? '',
            displayName: `${pl.externalPricelistRef} - ${pl.name}`
        }))
        .sort((a, b) => a.ref.localeCompare(b.ref));

    const map: { [key: number]: number } = {};
    const roots: ExternalPricelistTreeItem[] = [];

    for (let i = 0; i < externalPricelistTreeItems.length; i += 1) {
        map[externalPricelistTreeItems[i].id] = i;
    }

    for (const pricelist of externalPricelistTreeItems) {
        roots.push(pricelist);
    }
    return roots;
};

export const getSelectedExternalPricelist = createSelector(
    [getSelectedExternalPricelistId, getExternalPricelists],
    (selectedExternalPricelistId, externalPricelists) => {
        if (selectedExternalPricelistId == null) {
            return null;
        }

        return externalPricelists[selectedExternalPricelistId];
    }
);

export const getExternalPricelistPermissionsFromSelectedGroup = createSelector(
    [getSelectedUserGroup, getUserGroupPermissions, getAvailableResources, getExternalPricelists],
    (selectedUserGroup, userGroupPermissions, availableResources, extPricelists) => {
        if (selectedUserGroup == null) {
            return [];
        }

        const recordResources = availableResources.recordResources;

        const pricelistPermissions = selectedUserGroup.permissions
            .map(permissionId => {
                const permission = userGroupPermissions[permissionId];
                if (
                    permission == null ||
                    permission.resource !== ResourceType.ExternalPricelist ||
                    permission.recordId == null
                ) {
                    return null;
                }

                if (recordResources[permission.resource] == null) {
                    return null;
                }

                // 0 mean ext pricelists not created yet, e.g. permission for creating new ext pricelists
                if (permission.recordId === 0) {
                    return [0, permission] as const;
                }

                const extPricelist = extPricelists[permission.recordId];
                if (extPricelist == null) {
                    return null;
                }

                return [extPricelist.externalPricelistId, permission] as const;
            })
            .filter(notNull);

        const pricelistGroupPermissionObj: { [pricelistId: number]: UserGroupPermissionDto } = {};
        for (const [extPricelistId, permission] of pricelistPermissions) {
            pricelistGroupPermissionObj[extPricelistId] = permission;
        }

        return pricelistGroupPermissionObj;
    }
);

export const getSelectedExternalPricelistPermission = createSelector(
    [getSelectedExternalPricelistId, getCurrentUserPermissions],
    (selectedExtPricelistId, userPermissions) =>
        getPermission(ResourceType.ExternalPricelist, selectedExtPricelistId, userPermissions)
);
